/** "08:00" → "8:00 AM". The one 24h to 12h clock: HH:MM is wall-clock by
 *  storage convention, so this only reformats digits, with no time zone math.
 *
 *  Accepts `H:M` or `H:M:S`, pads a single-digit minute, and returns anything
 *  else UNCHANGED (`'99:00'` stays `'99:00'`) rather than inventing a time.
 *  Four app copies used to disagree on exactly that; they now re-export this. */
export function formatTime12(hhmm: string): string {
  const parts = time12Parts(hhmm)
  return parts ? `${parts.time} ${parts.period}` : hhmm
}

/** `formatTime12` in two pieces, for a layout that places the period itself
 *  (a practice range states it once; a PDF form has its own AM/PM box):
 *  "17:00" → { time: '5:00', period: 'PM' }. Null for anything formatTime12
 *  would return unchanged. */
export function time12Parts(hhmm: string): { time: string; period: 'AM' | 'PM' } | null {
  const parts = /^(\d{1,2}):(\d{1,2})(?::\d{1,2})?$/.exec(hhmm)
  if (!parts) return null
  const hour = Number(parts[1])
  const minute = Number(parts[2])
  if (hour > 23 || minute > 59) return null
  return { time: `${hour % 12 === 0 ? 12 : hour % 12}:${String(minute).padStart(2, '0')}`, period: hour < 12 ? 'AM' : 'PM' }
}

/** A stored wall-clock time: zero-padded 24h `HH:MM`, `00:00` to `23:59`.
 *  The validation every write route and form applies to a time field. */
const HHMM_RE = /^([01]\d|2[0-3]):[0-5]\d$/

export function isHHMM(value: string): boolean {
  return HHMM_RE.test(value)
}

/** Minutes since midnight. Lenient on the way in (`8:05` and `17:00:00`
 *  read fine) because it also parses InLeague and spreadsheet values; null
 *  for anything that is not a clock time, so a caller decides what a bad
 *  value means instead of getting a silent zero. */
export function hhmmToMinutes(value: string): number | null {
  const m = /^(\d{1,2}):(\d{2})(?::\d{2})?$/.exec(value)
  if (!m) return null
  const h = Number(m[1])
  const min = Number(m[2])
  return h > 23 || min > 59 ? null : h * 60 + min
}

/** `hhmmToMinutes` for a stored time already validated on write: NaN, not
 *  null, for a malformed one, so every comparison it reaches is false and a
 *  bad row drops out instead of reading as midnight. */
export function clockMinutes(hhmm: string): number {
  return hhmmToMinutes(hhmm) ?? Number.NaN
}

/** Minutes since midnight as zero-padded `HH:MM`. No wrap: 1440 is `24:00`,
 *  so a caller that must stay within the day checks the range itself.
 *  Expects a non-negative count. */
export function minutesToHHMM(total: number): string {
  return `${String(Math.floor(total / 60)).padStart(2, '0')}:${String(total % 60).padStart(2, '0')}`
}

const WEEKDAY_LONG = new Intl.DateTimeFormat('en-US', { weekday: 'long', timeZone: 'UTC' })

/** `Saturday` for a stored ISO date (`YYYY-MM-DD`), anchored in UTC like `gameDayLabel`. */
export function weekdayLong(dateISO: string): string {
  const [y, m, d] = dateISO.split('-').map(Number)
  return WEEKDAY_LONG.format(new Date(Date.UTC(y ?? 1970, (m ?? 1) - 1, d ?? 1)))
}

/** `Sat Sep 12` for a stored ISO date (`YYYY-MM-DD`).
 *
 *  ⚠️ No `new Date(dateISO)`: it parses as UTC midnight, and a host west of
 *  Greenwich names the day before. Anchor with `Date.UTC`, format in UTC.
 *  Anything that does not parse comes back unchanged, because a free-text date
 *  reaches this and `formatToParts` throws on an Invalid Date; a caller that
 *  needs a strict date checks first (`isIsoDate`). */
export function gameDayLabel(dateISO: string): string {
  const [y, m, d] = dateISO.split('-').map(Number)
  // ⚠️ `?? 1970` below does not catch this: NaN is not nullish.
  if (!Number.isFinite(y) || !Number.isFinite(m) || !Number.isFinite(d)) return dateISO
  const at = new Date(Date.UTC(y ?? 1970, (m ?? 1) - 1, d ?? 1))
  const parts = new Intl.DateTimeFormat('en-US', {
    weekday: 'short',
    month: 'short',
    day: 'numeric',
    timeZone: 'UTC',
  }).formatToParts(at)
  return parts
    .filter((p) => p.type === 'weekday' || p.type === 'month' || p.type === 'day')
    .map((p) => p.value)
    .join(' ')
}
